import { ReactNode, useCallback, useMemo } from "react";
import Table, { TableControls, TableFooter, type TableAction } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import Checkbox from "@/components/Checkbox";
import { ColumnDef } from "@tanstack/react-table";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useParams } from "@/hooks/useSearchParams";
import { type TableRowMenu } from "@/hooks/useTableRowMenu";
import type { useBulkSelection } from "@/hooks/useBulkSelection";
import { useNavigate } from "@/router";
import type { MemberElectionApplicationNode } from "../types";
import { buildMemberElectionPersonPath } from "../_utils/buildMemberElectionPersonPath";
import { buildMemberElectionApplicationColumns } from "./memberElectionApplicationTableColumns";
import MemberElectionsSearchInput from "./MemberElectionsSearchInput";

/** Local selection column so Member Elections can set accessible labels without changing the shared Table helper. */
function buildSelectionColumn(): ColumnDef<MemberElectionApplicationNode> {
  return {
    id: "select",
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    meta: {
      className: "pr-0",
      headerClassName: "pr-0"
    },
    header: ({ table }) => (
      <Checkbox
        name="select-all"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={(checked) => table.toggleAllRowsSelected(checked)}
        aria-label="Select all applicants on this page"
      />
    ),
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          name={`select-row-${row.id}`}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={(checked) => row.toggleSelected(checked)}
          aria-label={`Select ${row.original.person.displayName}`}
        />
      </div>
    )
  };
}

export type Props = {
  loading: boolean;
  applications: MemberElectionApplicationNode[];
  createHeaderPagination?: ReactNode;
  createFooterPagination?: ReactNode;
  bulkSelection: ReturnType<typeof useBulkSelection>;
  bulkActions: TableAction[];
  searchValue: string;
  setSearchValue: (value: string) => void;
  clearSearch: () => void;
  hasActiveSearch: boolean;
  setMenu: (menu: TableRowMenu) => void;
  onViewPerson?: (pgaId: string) => void;
};

export default function MemberElectionsTable({
  loading,
  applications,
  createHeaderPagination,
  createFooterPagination,
  bulkSelection,
  bulkActions,
  searchValue,
  setSearchValue,
  clearSearch,
  hasActiveSearch,
  setMenu,
  onViewPerson
}: Props) {
  const { isCompactLayout } = useIsCompactLayout();
  const navigate = useNavigate();
  const { searchParamsUrl } = useParams();
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll, rowSelectionCount } =
    bulkSelection;

  const handleNavigateToPerson = useCallback(
    (pgaId: string) => {
      const personPath = buildMemberElectionPersonPath(pgaId, searchParamsUrl, isCompactLayout);
      if (personPath) {
        navigate(personPath);
      }
    },
    [isCompactLayout, navigate, searchParamsUrl]
  );

  const columns = useMemo(
    () => [
      buildSelectionColumn(),
      ...buildMemberElectionApplicationColumns({
        isCompactLayout,
        setMenu,
        onViewPerson,
        onNavigateToPerson: handleNavigateToPerson
      })
    ],
    [handleNavigateToPerson, isCompactLayout, onViewPerson, setMenu]
  );

  const displayApplications = useMemo(() => {
    if (!isSelectedOnly) {
      return applications;
    }
    return applications.filter((row) => rowSelection[row.id]);
  }, [applications, isSelectedOnly, rowSelection]);

  return (
    <Table
      loading={loading}
      data={displayApplications}
      columns={columns}
      enableRowSelection
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      getRowLink={(row) => {
        const personPath = buildMemberElectionPersonPath(row.original.person.pgaId, searchParamsUrl, isCompactLayout);
        return personPath ? { to: personPath } : undefined;
      }}
      hasActiveFilters={hasActiveSearch || isSelectedOnly}
      data-testid="member-elections-table"
      renderControls={(table, { onSortClick } = {}) => (
        <TableControls
          pagination={!isSelectedOnly ? createHeaderPagination : undefined}
          onSortClick={onSortClick}
          bulkActions={bulkActions}
          {...bulkSelectionProps}
          onDeselectAll={deselectAll}
          totalRowCount={rowSelectionCount}
          onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
          allRowsSelected={table.getIsAllRowsSelected()}
          someRowsSelected={table.getIsSomeRowsSelected()}
        >
          <MemberElectionsSearchInput query={searchValue} onChange={setSearchValue} onClear={clearSearch} />
        </TableControls>
      )}
      renderFooter={() => (
        <TableFooter pagination={!isSelectedOnly ? createFooterPagination : undefined} {...bulkSelectionProps} />
      )}
      renderNoDataEmptyState={() => (
        <EmptyState
          iconName="how_to_vote"
          title="No-one Eligible for Member Election"
          caption="No one is currently eligible for member election. When member applications are approved, the applicants will automatically be added to this list."
        />
      )}
      renderFilteredEmptyState={() =>
        isSelectedOnly ? (
          <EmptyState
            iconName="how_to_vote"
            title="No Selected Rows on This Page"
            caption="Selected applicants on other pages are not shown here. Turn off Show Selected Only to return to the full list."
          />
        ) : (
          <EmptyState iconName="search" title="No Results Found" caption="Try a different search." />
        )
      }
    />
  );
}
